import React, { useEffect, useMemo, useRef } from 'react';
import { FaSearch } from 'react-icons/fa';
import {
  deriveSelectedTenants,
  getMainTenantStatus,
  getTenantDisplayName,
  matchesTenantSearch,
  UI_STATUS_OPTIONS,
} from '../tenantHelpers';

const TENANT_TYPE_OPTIONS = ['all', 'Internal', 'External', 'Technical', 'Pre-GA'];

const TenantFeatureTenantSelector = ({
  tenants,
  selectedTenantIds,
  onSelectedTenantIdsChange,
  labelOptions,
}) => {
  const headerSelectRef = useRef(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState('all');
  const [labelFilter, setLabelFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');

  const filteredTenants = useMemo(() => (
    (tenants || [])
      .filter((tenant) => typeFilter === 'all' || tenant.tenantType === typeFilter)
      .filter((tenant) => !statusFilter || getMainTenantStatus(tenant) === statusFilter)
      .filter((tenant) => !labelFilter || (tenant.labels || []).includes(labelFilter))
      .filter((tenant) => matchesTenantSearch(tenant, searchTerm))
  ), [tenants, typeFilter, statusFilter, labelFilter, searchTerm]);

  const selectedIds = selectedTenantIds || new Set();
  const selectedTenants = useMemo(
    () => deriveSelectedTenants(tenants, selectedIds),
    [tenants, selectedIds]
  );

  const visibleSelectedCount = filteredTenants.filter((tenant) => selectedIds.has(tenant.id)).length;
  const allVisibleSelected = filteredTenants.length > 0 && visibleSelectedCount === filteredTenants.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

  useEffect(() => {
    if (headerSelectRef.current) {
      headerSelectRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected, filteredTenants.length, selectedIds]);

  useEffect(() => {
    onSelectedTenantIdsChange?.((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set((tenants || []).map((tenant) => tenant.id));
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [tenants, onSelectedTenantIdsChange]);

  const toggleTenant = (tenant) => {
    if (!tenant?.id) return;
    onSelectedTenantIdsChange?.((prev) => {
      const next = new Set(prev);
      if (next.has(tenant.id)) next.delete(tenant.id);
      else next.add(tenant.id);
      return next;
    });
  };

  const toggleAllVisible = (selectAll) => {
    onSelectedTenantIdsChange?.((prev) => {
      const next = new Set(prev);
      filteredTenants.forEach((tenant) => {
        if (!tenant?.id) return;
        if (selectAll) next.add(tenant.id);
        else next.delete(tenant.id);
      });
      return next;
    });
  };

  const clearSelection = () => {
    onSelectedTenantIdsChange?.(new Set());
  };

  const formatLabelPreview = (labels) => {
    const list = labels || [];
    if (!list.length) return 'No labels';
    if (list.length <= 2) return list.join(' · ');
    return `${list.slice(0, 2).join(' · ')} +${list.length - 2}`;
  };

  return (
    <div className="stm-tenant-selector-panel">
      <div className="stm-tenant-selector-panel__head">
        <h3 className="stm-features-section__title">Select tenants</h3>
        <span
          className={`stm-tenant-selector-panel__count ${selectedTenants.length > 0 ? 'is-active' : ''}`}
        >
          {selectedTenants.length > 0
            ? `${selectedTenants.length} selected`
            : 'None selected'}
        </span>
      </div>

      <div className="stm-tenant-selector-panel__filters">
        <div className="stm-search stm-tenant-selector-panel__search">
          <FaSearch className="stm-search__icon" aria-hidden />
          <input
            className="stm-search__input"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search tenants"
            aria-label="Search tenants in feature manager"
          />
        </div>
        <div className="stm-tenant-selector-panel__filter-row">
          <select
            className="stm-tenant-selector-panel__select"
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
            aria-label="Filter by tenant type"
          >
            {TENANT_TYPE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option === 'all' ? 'All types' : option}
              </option>
            ))}
          </select>
          <select
            className="stm-tenant-selector-panel__select"
            value={labelFilter}
            onChange={(event) => setLabelFilter(event.target.value)}
            aria-label="Filter by label"
          >
            <option value="">All labels</option>
            {(labelOptions || []).map((label) => (
              <option key={label} value={label}>{label}</option>
            ))}
          </select>
        </div>
        <select
          className="stm-tenant-selector-panel__select stm-tenant-selector-panel__select--full"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {UI_STATUS_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>

      <div className="stm-tenant-selector-panel__list-toolbar">
        <label className="stm-tenant-selector-panel__select-visible">
          <input
            ref={headerSelectRef}
            type="checkbox"
            className="stm-table__checkbox"
            checked={allVisibleSelected}
            onChange={() => toggleAllVisible(!allVisibleSelected)}
            disabled={!filteredTenants.length}
          />
          Select visible
        </label>
        {selectedTenants.length > 0 && (
          <button type="button" className="stm-text-link" onClick={clearSelection}>
            Clear
          </button>
        )}
      </div>

      <div className="stm-tenant-selector-panel__list">
        {filteredTenants.length ? filteredTenants.map((tenant) => {
          const isSelected = selectedIds.has(tenant.id);
          return (
            <div
              key={tenant.id}
              className={`stm-tenant-pick-card ${isSelected ? 'is-selected' : ''}`}
              onClick={() => toggleTenant(tenant)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleTenant(tenant);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div
                className="stm-tenant-pick-card__check"
                onClick={(event) => event.stopPropagation()}
              >
                <input
                  type="checkbox"
                  className="stm-table__checkbox"
                  checked={isSelected}
                  onChange={() => toggleTenant(tenant)}
                  aria-label={`Select ${getTenantDisplayName(tenant)}`}
                />
              </div>
              <div className="stm-tenant-pick-card__body">
                <div className="stm-tenant-pick-card__title-row">
                  <strong>{getTenantDisplayName(tenant)}</strong>
                  <span className="stm-tenant-pick-card__type">{tenant.tenantType}</span>
                </div>
                <span className="stm-tenant-pick-card__labels">{formatLabelPreview(tenant.labels)}</span>
              </div>
            </div>
          );
        }) : (
          <p className="stm-muted stm-tenant-selector-panel__empty">
            No tenants match the current filters.
          </p>
        )}
      </div>
    </div>
  );
};

export default TenantFeatureTenantSelector;
